/** @format */
import * as _ from 'lodash-es';
import {
    Code,
    Education,
    EducationChildOption,
    EducationOption,
    EducationOptionAcceptanceType,
    EntityWithRule,
    LearningOpportunitySelectionPath,
    PersonalizedPhase,
    StudyRight,
} from '../../types/generated/common-backend.js';
import { OtmId, Urn } from '../../types/index.js';
import { ValidatablePlan } from '../plan/validation/validatablePlan.js';

export type PhaseName = 'phase1' | 'phase2';

export type EducationOptionType = 'EducationOption' | 'EducationChildOption';

export type EducationPathKey =
    | 'educationPhase1GroupId'
    | 'educationPhase1ChildGroupId'
    | 'educationPhase2GroupId'
    | 'educationPhase2ChildGroupId';

export enum EducationOptionStudyRightState {
    SELECTED_AS_MINOR = 'SELECTED_AS_MINOR',
    CONFIRMED = 'CONFIRMED',
    CONFLICTS_WITH_STUDY_RIGHT = 'CONFLICTS_WITH_STUDY_RIGHT',
    DUPLICATE_SELECTION = 'DUPLICATE_SELECTION',
    NOT_CONFIRMED = 'NOT_CONFIRMED',
    NO_STUDY_RIGHT = 'NO_STUDY_RIGHT',
}

export interface GenericPlanEducationOption {
    type: EducationOptionType;
    moduleGroupId: OtmId;
    educationPathKey: EducationPathKey;
    acceptanceType: EducationOptionAcceptanceType;
    moduleId: OtmId;
    isInPlan: boolean;
    isInPlanAsMinor: boolean;
    studyRightState: EducationOptionStudyRightState;
}

export interface PlanEducationOption extends GenericPlanEducationOption {
    phaseName: PhaseName;
    childOptionPathKey: EducationPathKey;
}

export interface PlanEducationChildOption extends GenericPlanEducationOption {
    parentModuleGroupId: OtmId;
    acceptanceType: EducationOptionAcceptanceType;
    namingType: Urn;
}

export interface PlanSelectionPathResult {
    selectionPath: LearningOpportunitySelectionPath;
    conflictingEducationPhase1ChildGroupIds: OtmId[];
    conflictingEducationPhase2ChildGroupIds: OtmId[];
    noDuplicates: boolean;
}

export class PlanStudyRightService {
    static readonly propertyNames = [
        'educationPhase1GroupId',
        'educationPhase1ChildGroupId',
        'educationPhase2GroupId',
        'educationPhase2ChildGroupId',
    ] as EducationPathKey[];

    static isPlanEducationOption(educationOption: GenericPlanEducationOption): educationOption is PlanEducationOption {
        return _.includes(['educationPhase1GroupId', 'educationPhase2GroupId'], educationOption.educationPathKey);
    }

    static isPlanEducationChildOption(educationOption: GenericPlanEducationOption): educationOption is PlanEducationChildOption {
        return _.includes(['educationPhase1ChildGroupId', 'educationPhase2ChildGroupId'], educationOption.educationPathKey);
    }

    static isPhase1Personalized(studyRight: StudyRight): boolean {
        return !_.isNil(_.get(studyRight, ['personalizedSelectionPath', 'phase1']));
    }

    static isPhase2Personalized(studyRight: StudyRight): boolean {
        return !_.isNil(_.get(studyRight, ['personalizedSelectionPath', 'phase2']));
    }

    static hasPersonalizedPhase1ModuleGroupId(studyRight: StudyRight): boolean {
        return !_.isNil(_.get(studyRight, ['personalizedSelectionPath', 'phase1', 'moduleGroupId']));
    }

    static hasPersonalizedPhase1ChildModuleGroupId(studyRight: StudyRight): boolean {
        return !_.isNil(_.get(studyRight, ['personalizedSelectionPath', 'phase1', 'childModuleGroupId']));
    }

    static hasPersonalizedPhase2ModuleGroupId(studyRight: StudyRight): boolean {
        return !_.isNil(_.get(studyRight, ['personalizedSelectionPath', 'phase2', 'moduleGroupId']));
    }

    static hasPersonalizedPhase2ChildModuleGroupId(studyRight: StudyRight): boolean {
        return !_.isNil(_.get(studyRight, ['personalizedSelectionPath', 'phase2', 'childModuleGroupId']));
    }

    static createPersonalizedAllowedPath = (
        allowedPath: LearningOpportunitySelectionPath,
        studyRight: StudyRight,
    ): LearningOpportunitySelectionPath => {
        const result = _.merge({}, allowedPath, {
            educationPhase1GroupId: _.get(studyRight, ['personalizedSelectionPath', 'phase1', 'moduleGroupId']),
            educationPhase1ChildGroupId: _.get(studyRight, ['personalizedSelectionPath', 'phase1', 'childModuleGroupId']),
            educationPhase2GroupId: _.get(studyRight, ['personalizedSelectionPath', 'phase2', 'moduleGroupId']),
            educationPhase2ChildGroupId: _.get(studyRight, ['personalizedSelectionPath', 'phase2', 'childModuleGroupId']),
        }) as LearningOpportunitySelectionPath;
        if (this.hasPersonalizedPhase1ModuleGroupId(studyRight) && !this.hasPersonalizedPhase1ChildModuleGroupId(studyRight)) {
            delete result.educationPhase1ChildGroupId;
        }
        if (this.hasPersonalizedPhase2ModuleGroupId(studyRight) && !this.hasPersonalizedPhase2ChildModuleGroupId(studyRight)) {
            delete result.educationPhase2ChildGroupId;
        }
        return result;
    };

    static addChildOptions(
        educationOption: EducationOption,
        educationOptionModule: EntityWithRule,
        educationPathKey: EducationPathKey,
        validatablePlan: ValidatablePlan,
        educationOptions: GenericPlanEducationOption[],
    ) {
        _.forEach(educationOption.childOptions, educationChildOption => {
            const childObject = this.getEducationChildOptionObject(
                educationChildOption,
                validatablePlan,
                educationOption,
                educationOptionModule,
                educationPathKey,
            );
            educationOptions.push(childObject);
        });
    }

    static createPersonalizedChildOption(
        education: Education,
        validatablePlan: ValidatablePlan,
        personalizedPhase: PersonalizedPhase,
        parentOption: PlanEducationOption,
        educationPathKey: EducationPathKey,
    ): PlanEducationChildOption {
        const childObject = {
            type: 'EducationChildOption',
            moduleGroupId: personalizedPhase.childModuleGroupId,
            parentModuleGroupId: personalizedPhase.moduleGroupId,
            educationPathKey,
            acceptanceType: 'ACCEPTED_BY_TEACHER',
            namingType: personalizedPhase.childNamingUrn,
        } as PlanEducationChildOption;
        const educationChildOptionModule = _.find(validatablePlan.modulesById, { groupId: personalizedPhase.childModuleGroupId });
        if (
            educationChildOptionModule &&
            validatablePlan.isModuleInPlan(educationChildOptionModule.id) &&
            validatablePlan.isModuleInPlanUnderModule(educationChildOptionModule.id, _.get(parentOption, 'moduleId'))
        ) {
            childObject.moduleId = educationChildOptionModule.id;
            childObject.isInPlan = true;
            childObject.isInPlanAsMinor = this.isModuleSelectedAsMinor(educationChildOptionModule, validatablePlan);
        } else {
            childObject.isInPlan = false;
            childObject.isInPlanAsMinor = false;
        }
        return childObject;
    }

    static createEducationPathKey(phase: PhaseName): EducationPathKey {
        return phase === 'phase1' ? 'educationPhase1GroupId' : 'educationPhase2GroupId';
    }

    static createChildOptionPathKey(phase: PhaseName): EducationPathKey {
        return phase === 'phase1' ? 'educationPhase1ChildGroupId' : 'educationPhase2ChildGroupId';
    }

    static createObjectFromEducationOption(
        educationOption: EducationOption,
        educationOptionModule: EntityWithRule,
        validatablePlan: ValidatablePlan,
        phaseName: PhaseName,
    ): PlanEducationOption {
        const object = {
            type: 'EducationOption',
            moduleGroupId: educationOption.moduleGroupId,
            educationPathKey: this.createEducationPathKey(phaseName),
            phaseName,
            acceptanceType: educationOption.acceptanceType,
        } as PlanEducationOption;
        if (!_.isEmpty(educationOption.childOptions)) {
            object.childOptionPathKey = this.createChildOptionPathKey(phaseName);
        }
        if (educationOptionModule && validatablePlan.isModuleInPlan(educationOptionModule.id)) {
            object.moduleId = educationOptionModule.id;
            object.isInPlan = true;
            object.isInPlanAsMinor = this.isModuleSelectedAsMinor(educationOptionModule, validatablePlan);
        } else {
            object.isInPlan = false;
            object.isInPlanAsMinor = false;
        }
        return object;
    }

    static createPersonalizedOption(
        education: Education,
        educationOptionModule: EntityWithRule,
        validatablePlan: ValidatablePlan,
        phase: PersonalizedPhase,
        phaseName: PhaseName,
    ): PlanEducationOption {
        const object = {
            type: 'EducationOption',
            moduleGroupId: phase.moduleGroupId,
            educationPathKey: this.createEducationPathKey(phaseName),
            phaseName,
            acceptanceType: 'ACCEPTED_BY_TEACHER',
        } as PlanEducationOption;
        if (educationOptionModule && validatablePlan.isModuleInPlan(educationOptionModule.id)) {
            object.moduleId = educationOptionModule.id;
            object.isInPlan = true;
            object.isInPlanAsMinor = this.isModuleSelectedAsMinor(educationOptionModule, validatablePlan);
        } else {
            object.isInPlan = false;
            object.isInPlanAsMinor = false;
        }
        return object;
    }

    static createPhase1ObjectFromEducationOption(
        educationOption: EducationOption,
        educationOptionModule: EntityWithRule,
        validatablePlan: ValidatablePlan,
    ): PlanEducationOption {
        return this.createObjectFromEducationOption(educationOption, educationOptionModule, validatablePlan, 'phase1');
    }

    static createPhase2ObjectFromEducationOption(
        educationOption: EducationOption,
        educationOptionModule: EntityWithRule,
        validatablePlan: ValidatablePlan,
    ): PlanEducationOption {
        return this.createObjectFromEducationOption(educationOption, educationOptionModule, validatablePlan, 'phase2');
    }

    static addPhase1Options(education: Education, validatablePlan: ValidatablePlan, educationOptions: GenericPlanEducationOption[]): void {
        _.forEach(education.structure.phase1.options, educationOption => {
            const educationOptionModule = _.find(validatablePlan.modulesById, { groupId: educationOption.moduleGroupId });
            this.addChildOptions(educationOption, educationOptionModule, 'educationPhase1ChildGroupId', validatablePlan, educationOptions);
            educationOptions.push(this.createPhase1ObjectFromEducationOption(educationOption, educationOptionModule, validatablePlan));
        });
    }

    static addPhase2Options(education: Education, validatablePlan: ValidatablePlan, educationOptions: GenericPlanEducationOption[]): void {
        _.forEach(education.structure.phase2.options, educationOption => {
            const educationOptionModule = _.find(validatablePlan.modulesById, { groupId: educationOption.moduleGroupId });
            this.addChildOptions(educationOption, educationOptionModule, 'educationPhase2ChildGroupId', validatablePlan, educationOptions);
            educationOptions.push(this.createPhase2ObjectFromEducationOption(educationOption, educationOptionModule, validatablePlan));
        });
    }

    static createPersonalizedPhase1Option(education: Education, validatablePlan: ValidatablePlan): PlanEducationOption {
        return this.createPersonalizedOption(
            education,
            _.find(validatablePlan.modulesById, {
                groupId: _.get(validatablePlan, ['studyRight', 'personalizedSelectionPath', 'phase1', 'moduleGroupId']),
            }),
            validatablePlan,
            _.get(validatablePlan, ['studyRight', 'personalizedSelectionPath', 'phase1']),
            'phase1',
        );
    }

    static createPersonalizedPhase2Option(education: Education, validatablePlan: ValidatablePlan): PlanEducationOption {
        return this.createPersonalizedOption(
            education,
            _.find(validatablePlan.modulesById, {
                groupId: _.get(validatablePlan, ['studyRight', 'personalizedSelectionPath', 'phase2', 'moduleGroupId']),
            }),
            validatablePlan,
            _.get(validatablePlan, ['studyRight', 'personalizedSelectionPath', 'phase2']),
            'phase2',
        );
    }

    static addPersonalizedPhase1Options(
        education: Education,
        validatablePlan: ValidatablePlan,
        educationOptions: GenericPlanEducationOption[],
    ): void {
        const option = this.createPersonalizedPhase1Option(education, validatablePlan);
        if (this.hasPersonalizedPhase1ChildModuleGroupId(validatablePlan.studyRight)) {
            educationOptions.push(
                this.createPersonalizedChildOption(
                    education,
                    validatablePlan,
                    _.get(validatablePlan, ['studyRight', 'personalizedSelectionPath', 'phase1']),
                    option,
                    this.createChildOptionPathKey('phase1'),
                ),
            );
        }
        educationOptions.push(option);
    }

    static addPersonalizedPhase2Options(
        education: Education,
        validatablePlan: ValidatablePlan,
        educationOptions: GenericPlanEducationOption[],
    ): void {
        const option = this.createPersonalizedPhase2Option(education, validatablePlan);
        if (this.hasPersonalizedPhase2ChildModuleGroupId(validatablePlan.studyRight)) {
            educationOptions.push(
                this.createPersonalizedChildOption(
                    education,
                    validatablePlan,
                    _.get(validatablePlan, ['studyRight', 'personalizedSelectionPath', 'phase2']),
                    option,
                    this.createChildOptionPathKey('phase2'),
                ),
            );
        }
        educationOptions.push(option);
    }

    static setSelectionPathInPlanForPersonalizedPhase(
        selectionPathInPlan: LearningOpportunitySelectionPath,
        phaseKey: string,
        personalizedPhase: PersonalizedPhase,
        validatablePlan: ValidatablePlan,
    ): void {
        const optionModule = _.find(validatablePlan.modulesById, { groupId: personalizedPhase.moduleGroupId });
        if (
            optionModule &&
            validatablePlan.isModuleInPlan(optionModule.id) &&
            !this.isModuleSelectedAsMinor(optionModule, validatablePlan)
        ) {
            _.set(selectionPathInPlan, `education${phaseKey}GroupId`, personalizedPhase.moduleGroupId);
            const childOptionModule = _.find(validatablePlan.modulesById, { groupId: personalizedPhase.childModuleGroupId });
            const childOptionModuleId = _.get(childOptionModule, 'id');
            if (
                validatablePlan.isModulePlannedOrAttainedUnderModule(
                    childOptionModuleId,
                    personalizedPhase.childModuleGroupId,
                    optionModule.id,
                ) &&
                !this.isModuleSelectedAsMinor(childOptionModule, validatablePlan)
            ) {
                _.set(selectionPathInPlan, `education${phaseKey}ChildGroupId`, personalizedPhase.childModuleGroupId);
            }
        }
    }

    static createPersonalizedAllowedPaths(
        allowedPaths: LearningOpportunitySelectionPath[],
        studyRight: StudyRight,
    ): LearningOpportunitySelectionPath[] {
        return _.uniqWith(
            _.map(allowedPaths, allowedPath => this.createPersonalizedAllowedPath(allowedPath, studyRight)),
            _.isEqual,
        );
    }

    static getSelectionPathInPlan(validatablePlan: ValidatablePlan, education: Education, studyRight: StudyRight): PlanSelectionPathResult {
        const selectionPath = {} as LearningOpportunitySelectionPath;
        let noDuplicates = true;
        const conflictingEducationPhase1ChildGroupIds: OtmId[] = [];
        const conflictingEducationPhase2ChildGroupIds: OtmId[] = [];
        const personalizedPhase1 = _.get(studyRight, 'personalizedSelectionPath.phase1');
        const personalizedPhase2 = _.get(studyRight, 'personalizedSelectionPath.phase2');
        if (!_.isNil(personalizedPhase1)) {
            this.setSelectionPathInPlanForPersonalizedPhase(selectionPath, 'Phase1', personalizedPhase1, validatablePlan);
        } else {
            _.forEach(education.structure.phase1.options, (educationOption: EducationOption) => {
                const optionModule = _.find(validatablePlan.modulesById, { groupId: educationOption.moduleGroupId });
                if (
                    optionModule &&
                    validatablePlan.isModuleInPlan(optionModule.id) &&
                    !this.isModuleSelectedAsMinor(optionModule, validatablePlan)
                ) {
                    selectionPath.educationPhase1GroupId = educationOption.moduleGroupId;
                    _.forEach(educationOption.childOptions, childOption => {
                        const childOptionModule = _.find(validatablePlan.modulesById, { groupId: childOption.moduleGroupId });
                        const childOptionModuleId = _.get(childOptionModule, 'id');
                        if (
                            validatablePlan.isModulePlannedOrAttainedUnderModule(
                                childOptionModuleId,
                                childOption.moduleGroupId,
                                optionModule.id,
                            ) &&
                            !this.isModuleSelectedAsMinor(childOptionModule, validatablePlan)
                        ) {
                            if (_.get(selectionPath, 'educationPhase1ChildGroupId')) {
                                noDuplicates = false;
                                conflictingEducationPhase1ChildGroupIds.push(selectionPath.educationPhase1ChildGroupId);
                                conflictingEducationPhase1ChildGroupIds.push(childOption.moduleGroupId);
                            } else {
                                selectionPath.educationPhase1ChildGroupId = childOption.moduleGroupId;
                            }
                        }
                    });
                }
            });
        }
        if (education.structure.phase2) {
            if (!_.isNil(personalizedPhase2)) {
                this.setSelectionPathInPlanForPersonalizedPhase(selectionPath, 'Phase2', personalizedPhase2, validatablePlan);
            } else {
                _.forEach(education.structure.phase2.options, educationOption => {
                    const optionModule = _.find(validatablePlan.modulesById, { groupId: educationOption.moduleGroupId });
                    if (
                        optionModule &&
                        validatablePlan.isModuleInPlan(optionModule.id) &&
                        !this.isModuleSelectedAsMinor(optionModule, validatablePlan)
                    ) {
                        selectionPath.educationPhase2GroupId = educationOption.moduleGroupId;
                        _.forEach(educationOption.childOptions, childOption => {
                            const childOptionModule = _.find(validatablePlan.modulesById, { groupId: childOption.moduleGroupId });
                            const childOptionModuleId = _.get(childOptionModule, 'id');
                            if (
                                validatablePlan.isModulePlannedOrAttainedUnderModule(
                                    childOptionModuleId,
                                    childOption.moduleGroupId,
                                    optionModule.id,
                                ) &&
                                !this.isModuleSelectedAsMinor(childOptionModule, validatablePlan)
                            ) {
                                if (_.get(selectionPath, 'educationPhase2ChildGroupId')) {
                                    noDuplicates = false;
                                    conflictingEducationPhase2ChildGroupIds.push(selectionPath.educationPhase2ChildGroupId);
                                    conflictingEducationPhase2ChildGroupIds.push(childOption.moduleGroupId);
                                } else {
                                    selectionPath.educationPhase2ChildGroupId = childOption.moduleGroupId;
                                }
                            }
                        });
                    }
                });
            }
        }

        const result = {
            selectionPath,
            conflictingEducationPhase1ChildGroupIds,
            conflictingEducationPhase2ChildGroupIds,
            noDuplicates,
        };
        if (studyRight) {
            this.resolveConflictingSelections(result, studyRight);
        }
        return result;
    }

    static resolveConflictingSelections(planSelectionPathResult: PlanSelectionPathResult, studyRight: StudyRight): void {
        const acceptedPathPhase1ChildGroupId = _.get(studyRight.acceptedSelectionPath, 'educationPhase1ChildGroupId');
        if (acceptedPathPhase1ChildGroupId) {
            const matchingModuleGroupId = _.find(
                planSelectionPathResult.conflictingEducationPhase1ChildGroupIds,
                moduleGroupId => moduleGroupId === acceptedPathPhase1ChildGroupId,
            );
            if (matchingModuleGroupId) {
                _.set(planSelectionPathResult.selectionPath, 'educationPhase1ChildGroupId', matchingModuleGroupId);
            }
        }

        const acceptedPathPhase2ChildGroupId = _.get(studyRight.acceptedSelectionPath, 'educationPhase2ChildGroupId');
        if (acceptedPathPhase2ChildGroupId) {
            const matchingModuleGroupId = _.find(
                planSelectionPathResult.conflictingEducationPhase2ChildGroupIds,
                moduleGroupId => moduleGroupId === acceptedPathPhase2ChildGroupId,
            );
            if (matchingModuleGroupId) {
                _.set(planSelectionPathResult.selectionPath, 'educationPhase2ChildGroupId', matchingModuleGroupId);
            }
        }
    }

    static getStudyRightSelectionPaths(education: Education, studyRight: StudyRight): LearningOpportunitySelectionPath[] {
        const studyRightLearningOpportunity = _.find(education.structure.learningOpportunities, {
            localId: studyRight.learningOpportunityId,
        });
        if (!_.isNil(_.get(studyRight, 'personalizedSelectionPath'))) {
            return this.createPersonalizedAllowedPaths(_.get(studyRightLearningOpportunity, 'allowedPaths'), studyRight);
        }
        if (!studyRightLearningOpportunity) {
            return [];
        }
        return _.get(studyRightLearningOpportunity, 'allowedPaths');
    }

    static selectionPathIsPartialMatch(
        partialPath: LearningOpportunitySelectionPath,
        completePath: LearningOpportunitySelectionPath,
    ): boolean {
        const allKeysMatch = _.every(_.keys(partialPath), key => _.get(partialPath, key) === _.get(completePath, key));
        if (!allKeysMatch) {
            return false;
        }
        const partialPathIndexes = _.map(_.keys(partialPath), key => _.indexOf(this.propertyNames, key));
        const completePathIndexes = _.map(_.keys(completePath), key => _.indexOf(this.propertyNames, key));
        if (_.min(_.xor(partialPathIndexes, completePathIndexes)) < _.max(partialPathIndexes)) {
            return false;
        }
        return true;
    }

    static getSelectionPathMaxIndex(selectionPath: LearningOpportunitySelectionPath): number {
        if (_.get(selectionPath, 'educationPhase2ChildGroupId')) {
            return 3;
        }
        if (_.get(selectionPath, 'educationPhase2GroupId')) {
            return 2;
        }
        if (_.get(selectionPath, 'educationPhase1ChildGroupId')) {
            return 1;
        }
        if (_.get(selectionPath, 'educationPhase1GroupId')) {
            return 0;
        }
        return -1;
    }

    static isChildOption(educationOption: PlanEducationOption | PlanEducationChildOption): boolean {
        return _.indexOf(this.propertyNames, educationOption.educationPathKey) % 2 !== 0;
    }

    static matchesCorrespondingPropertyInSomeAllowedPath(
        educationOption: GenericPlanEducationOption,
        studyRight: StudyRight,
        education: Education,
    ) {
        const studyRightSelectionPaths = this.getStudyRightSelectionPaths(education, studyRight);
        if (studyRightSelectionPaths.length === 0) {
            return false;
        }
        const possiblePaths = _.filter(studyRightSelectionPaths, allowedPath =>
            this.selectionPathIsPartialMatch(studyRight.acceptedSelectionPath, allowedPath),
        );
        return _.some(possiblePaths, allowedPath => this.matchesCorrespondingPropertyInSelectionPath(educationOption, allowedPath));
    }

    static matchesCorrespondingPropertyInSelectionPath(
        educationOption: GenericPlanEducationOption,
        selectionPath: LearningOpportunitySelectionPath,
    ): boolean {
        if (!this.isPlanEducationChildOption(educationOption)) {
            return _.get(selectionPath, educationOption.educationPathKey) === educationOption.moduleGroupId;
        }
        const educationChildOption = educationOption as PlanEducationChildOption;
        const parentPathKey = this.getPreviousEducationPathKey(educationChildOption.educationPathKey);
        const parentMatches = _.get(selectionPath, parentPathKey) === educationChildOption.parentModuleGroupId;
        const childMatches = _.get(selectionPath, educationChildOption.educationPathKey) === educationChildOption.moduleGroupId;
        return parentMatches && childMatches;
    }

    static hasSelectionPathAGap(selectionPath: LearningOpportunitySelectionPath, educationOptionKey: EducationPathKey) {
        return (
            !_.has(selectionPath, educationOptionKey) &&
            this.getSelectionPathMaxIndex(selectionPath) > _.indexOf(this.propertyNames, educationOptionKey)
        );
    }

    static getStudyRightStateForEducationOption(
        educationOption: GenericPlanEducationOption,
        education: Education,
        selectionPathInPlan: PlanSelectionPathResult,
        studyRight: StudyRight,
    ): EducationOptionStudyRightState {
        if (educationOption.isInPlanAsMinor) {
            return EducationOptionStudyRightState.SELECTED_AS_MINOR;
        }
        if (_.has(studyRight.acceptedSelectionPath, educationOption.educationPathKey)) {
            return this.matchesCorrespondingPropertyInSelectionPath(educationOption, studyRight.acceptedSelectionPath)
                ? EducationOptionStudyRightState.CONFIRMED
                : EducationOptionStudyRightState.CONFLICTS_WITH_STUDY_RIGHT;
        }
        if (this.hasSelectionPathAGap(studyRight.acceptedSelectionPath, educationOption.educationPathKey)) {
            return EducationOptionStudyRightState.CONFLICTS_WITH_STUDY_RIGHT;
        }
        if (
            educationOption.educationPathKey === 'educationPhase1ChildGroupId' &&
            !_.isEmpty(selectionPathInPlan.conflictingEducationPhase1ChildGroupIds)
        ) {
            return EducationOptionStudyRightState.DUPLICATE_SELECTION;
        }
        if (
            educationOption.educationPathKey === 'educationPhase2ChildGroupId' &&
            !_.isEmpty(selectionPathInPlan.conflictingEducationPhase2ChildGroupIds)
        ) {
            return EducationOptionStudyRightState.DUPLICATE_SELECTION;
        }
        if (!this.matchesCorrespondingPropertyInSomeAllowedPath(educationOption, studyRight, education)) {
            return EducationOptionStudyRightState.CONFLICTS_WITH_STUDY_RIGHT;
        }
        if (educationOption.acceptanceType === 'AUTOMATIC') {
            return EducationOptionStudyRightState.NOT_CONFIRMED;
        }
        return EducationOptionStudyRightState.NO_STUDY_RIGHT;
    }

    static getValidatedEducationOptions(validatablePlan: ValidatablePlan, education: Education, studyRight: StudyRight) {
        if (!studyRight) {
            return null;
        }
        const educationOptions = this.getEducationOptions(validatablePlan, education);

        const selectionPath = this.getSelectionPathInPlan(validatablePlan, education, studyRight);
        _.forEach(educationOptions, educationOption => {
            educationOption.studyRightState = this.getStudyRightStateForEducationOption(
                educationOption,
                education,
                selectionPath,
                studyRight,
            );
        });

        return educationOptions;
    }

    static populateNamingData(
        educationOptions: GenericPlanEducationOption[],
        education: Education,
        educationOptionNamingTypes: { [id: string]: Code },
    ): GenericPlanEducationOption[] {
        _.forEach(educationOptions, educationOption => {
            if (this.isPlanEducationChildOption(educationOption)) {
                _.set(educationOption, 'naming', _.get(educationOptionNamingTypes, educationOption.namingType).name);
            }
            if (this.isPlanEducationOption(educationOption)) {
                const phase = _.get(education.structure, _.get(educationOption, 'phaseName'));
                _.set(educationOption, 'naming', _.get(phase, 'name'));
            }
        });
        return educationOptions;
    }

    static getEducationChildOptionObject(
        educationChildOption: EducationChildOption,
        validatablePlan: ValidatablePlan,
        parentOption: EducationOption,
        parentModule: EntityWithRule,
        educationPathKey: EducationPathKey,
    ) {
        const childObject = {
            type: 'EducationChildOption',
            moduleGroupId: educationChildOption.moduleGroupId,
            parentModuleGroupId: parentOption.moduleGroupId,
            educationPathKey,
            acceptanceType: educationChildOption.acceptanceType,
            namingType: parentOption.childNaming,
        } as PlanEducationChildOption;
        const educationChildOptionModule = _.find(validatablePlan.modulesById, { groupId: educationChildOption.moduleGroupId });
        if (
            educationChildOptionModule &&
            validatablePlan.isModuleInPlan(educationChildOptionModule.id) &&
            validatablePlan.isModuleInPlanUnderModule(educationChildOptionModule.id, _.get(parentModule, 'id'))
        ) {
            childObject.moduleId = educationChildOptionModule.id;
            childObject.isInPlan = true;
            childObject.isInPlanAsMinor = this.isModuleSelectedAsMinor(educationChildOptionModule, validatablePlan);
        } else {
            childObject.isInPlan = false;
            childObject.isInPlanAsMinor = false;
        }
        return childObject;
    }

    static getEducationOptions(validatablePlan: ValidatablePlan, education: Education) {
        const educationOptions: GenericPlanEducationOption[] = [];
        if (this.isPhase1Personalized(validatablePlan.studyRight)) {
            this.addPersonalizedPhase1Options(education, validatablePlan, educationOptions);
        } else {
            this.addPhase1Options(education, validatablePlan, educationOptions);
        }
        if (education.structure.phase2) {
            if (this.isPhase2Personalized(validatablePlan.studyRight)) {
                this.addPersonalizedPhase2Options(education, validatablePlan, educationOptions);
            } else {
                this.addPhase2Options(education, validatablePlan, educationOptions);
            }
        }

        return educationOptions;
    }

    static getMatchingEducationOption(
        moduleGroupId: OtmId,
        educationOptions: GenericPlanEducationOption[],
        validatablePlan: ValidatablePlan,
    ) {
        const matchingEducationOption = _.find(educationOptions, educationOption => {
            if (moduleGroupId !== educationOption.moduleGroupId) {
                return false;
            }
            if (this.isPlanEducationChildOption(educationOption)) {
                const module = validatablePlan.getModuleInPlanByGroupId(moduleGroupId);
                const parentModule = validatablePlan.getModuleInPlanByGroupId(_.get(educationOption, 'parentModuleGroupId'));
                if (this.doesModuleContainOnlyMinors(parentModule)) {
                    return false;
                }
                if (module && parentModule) {
                    return validatablePlan.isModuleInPlanUnderModule(module.id, parentModule.id);
                }
                return false;
            }
            return true;
        });
        return matchingEducationOption;
    }

    /*
        getPotentialEducationOption returns an educationOption that would match the module referred to by
        moduleGroupId if it were in plan under the module referred to by parentModuleGroupId
    */

    static getPotentialEducationOption(
        moduleGroupId: OtmId,
        parentModuleGroupId: OtmId,
        educationOptions: GenericPlanEducationOption[],
        validatablePlan: ValidatablePlan,
    ) {
        const potentialEducationOption = _.find(educationOptions, educationOption => {
            if (moduleGroupId !== educationOption.moduleGroupId) {
                return false;
            }
            if (this.isPlanEducationChildOption(educationOption)) {
                const module = validatablePlan.getModuleInPlanByGroupId(parentModuleGroupId);
                const parentModule = validatablePlan.getModuleInPlanByGroupId(_.get(educationOption, 'parentModuleGroupId'));
                if (this.doesModuleContainOnlyMinors(parentModule)) {
                    return false;
                }
                if (module && parentModule) {
                    return validatablePlan.isModuleInPlanUnderModule(module.id, parentModule.id);
                }
                return false;
            }
            return true;
        });
        return potentialEducationOption;
    }

    static planConflictsWithStudyRight(educationOptions: GenericPlanEducationOption[]) {
        const conflictingOption = _.find(
            educationOptions,
            option => option.isInPlan && option.studyRightState === EducationOptionStudyRightState.CONFLICTS_WITH_STUDY_RIGHT,
        );
        return !!conflictingOption;
    }

    static getNextSelectableLevel(studyRight: StudyRight, education: Education) {
        let increment;
        const selectionPathMaxIndex = this.getSelectionPathMaxIndex(studyRight.acceptedSelectionPath);
        if (selectionPathMaxIndex === -1) {
            return 0;
        }
        const selectionPathLastKey = _.nth(this.propertyNames, selectionPathMaxIndex);
        const phase = selectionPathMaxIndex < 2 ? education.structure.phase1 : education.structure.phase2;
        if (selectionPathMaxIndex % 2 === 0) {
            const educationOption = _.find(
                phase.options,
                option => option.moduleGroupId === _.get(studyRight.acceptedSelectionPath, selectionPathLastKey),
            );
            increment = _.isNil(educationOption) || _.isEmpty(educationOption.childOptions) ? 2 : 1;
        } else {
            increment = 1;
        }
        const result = selectionPathMaxIndex + increment;
        return result < 4 ? result : null;
    }

    static pickPhase2OfSelectionPath(selectionPath: LearningOpportunitySelectionPath) {
        return _.pick(selectionPath, ['educationPhase2GroupId', 'educationPhase2ChildGroupId']) as LearningOpportunitySelectionPath;
    }

    static canEducationOptionBeSelected(educationOption: GenericPlanEducationOption, studyRight: StudyRight, education: Education) {
        const educationOptionIndex = _.indexOf(this.propertyNames, educationOption.educationPathKey);
        const hasPersonalizedPhase1 = !_.isNil(_.get(studyRight, 'personalizedSelectionPath.phase1'));
        const hasPersonalizedPhase2 = !_.isNil(_.get(studyRight, 'personalizedSelectionPath.phase2'));
        if ((hasPersonalizedPhase1 && educationOptionIndex < 2) || hasPersonalizedPhase2) {
            return false;
        }
        const selectionPathMaxIndex = this.getSelectionPathMaxIndex(studyRight.acceptedSelectionPath);
        const selectionPathToBeSelected = _.clone(studyRight.acceptedSelectionPath);
        if (educationOptionIndex === selectionPathMaxIndex || educationOptionIndex === this.getNextSelectableLevel(studyRight, education)) {
            _.set(selectionPathToBeSelected, educationOption.educationPathKey, educationOption.moduleGroupId);
            if (this.isPlanEducationChildOption(educationOption)) {
                const parentPathKey = this.getPreviousEducationPathKey(educationOption.educationPathKey);
                if (_.get(studyRight, ['acceptedSelectionPath', parentPathKey]) !== educationOption.parentModuleGroupId) {
                    return false;
                }
            }
            return _.some(this.getStudyRightSelectionPaths(education, studyRight), studyRightSelectionPath => {
                if (hasPersonalizedPhase1) {
                    return this.selectionPathIsPartialMatch(
                        this.pickPhase2OfSelectionPath(selectionPathToBeSelected),
                        this.pickPhase2OfSelectionPath(studyRightSelectionPath),
                    );
                }
                return this.selectionPathIsPartialMatch(selectionPathToBeSelected, studyRightSelectionPath);
            });
        }
        return false;
    }

    static isEducationOptionSelected(educationOption: GenericPlanEducationOption, studyRight: StudyRight) {
        const optionMatchesSelectionPath = this.matchesCorrespondingPropertyInSelectionPath(
            educationOption,
            studyRight.acceptedSelectionPath,
        );
        if (this.isPlanEducationOption(educationOption)) {
            // educationOption is not childOption
            return !!optionMatchesSelectionPath;
        }
        if (this.isPlanEducationChildOption(educationOption)) {
            const parentEducationOption = {
                educationPathKey: this.getPreviousEducationPathKey(educationOption.educationPathKey),
                moduleGroupId: educationOption.parentModuleGroupId,
            } as PlanEducationOption;
            const parentOptionMatchesSelectionPath = this.matchesCorrespondingPropertyInSelectionPath(
                parentEducationOption,
                studyRight.acceptedSelectionPath,
            );
            return !!optionMatchesSelectionPath && !!parentOptionMatchesSelectionPath;
        }
    }

    static getPreviousEducationPathKey(educationPathKey: EducationPathKey) {
        return _.nth(this.propertyNames, _.indexOf(this.propertyNames, educationPathKey) - 1);
    }

    static getNextEducationPathKey(educationPathKey: EducationPathKey) {
        return _.nth(this.propertyNames, _.indexOf(this.propertyNames, educationPathKey) + 1);
    }

    static getFirstNotConfirmedSelectionPathKeyInPlan(
        selectionPathInPlan: LearningOpportunitySelectionPath,
        studyRight: StudyRight,
        education: Education,
    ) {
        return _.find(this.propertyNames, educationPathKey => {
            if (educationPathKey === 'educationPhase1ChildGroupId' && !this.isPhase1ChildGroupIdRequired(selectionPathInPlan, education)) {
                return false;
            }
            const planModuleGroupId = _.get(selectionPathInPlan, educationPathKey);
            const studyRightModuleGroupId = _.get(studyRight.acceptedSelectionPath, educationPathKey);
            if (!studyRightModuleGroupId || !planModuleGroupId || studyRightModuleGroupId !== planModuleGroupId) {
                return true;
            }
            return false;
        });
    }

    static isPhase1ChildGroupIdRequired(selectionPath: LearningOpportunitySelectionPath, education: Education) {
        const parentModuleGroupId = _.get(selectionPath, 'educationPhase1GroupId');
        if (!parentModuleGroupId) {
            return false;
        }
        const parentEducationOption = _.find(education.structure.phase1.options, { moduleGroupId: parentModuleGroupId });
        return parentEducationOption ? !_.isEmpty(parentEducationOption.childOptions) : false;
    }

    static getFirstNotConfirmedEducationOption(
        educationOptions: GenericPlanEducationOption[],
        selectionPathInPlan: LearningOpportunitySelectionPath,
        studyRight: StudyRight,
        education: Education,
    ) {
        const confirmationTargetKey = this.getFirstNotConfirmedSelectionPathKeyInPlan(selectionPathInPlan, studyRight, education);
        if (!confirmationTargetKey) {
            return null;
        }
        if (_.indexOf(this.propertyNames, confirmationTargetKey) % 2 === 0) {
            return _.find(
                educationOptions,
                educationOption =>
                    _.get(educationOption, 'educationPathKey') === confirmationTargetKey &&
                    _.get(educationOption, 'moduleGroupId') === _.get(selectionPathInPlan, confirmationTargetKey) &&
                    !_.get(educationOption, 'isInPlanAsMinor'),
            );
        }

        return _.find(
            educationOptions,
            educationOption =>
                _.get(educationOption, 'educationPathKey') === confirmationTargetKey &&
                _.get(educationOption, 'parentModuleGroupId') ===
                    _.get(selectionPathInPlan, this.getPreviousEducationPathKey(confirmationTargetKey)) &&
                _.get(educationOption, 'moduleGroupId') === _.get(selectionPathInPlan, confirmationTargetKey) &&
                !_.get(educationOption, 'isInPlanAsMinor'),
        );
    }

    static isEducationOptionSelectableBasedOnLearningOpportunity(
        educationOption: GenericPlanEducationOption,
        education: Education,
        learningOpportunityId: string,
    ) {
        const learningOpportunity = _.find(education.structure.learningOpportunities, { localId: learningOpportunityId });
        if (!learningOpportunity) {
            return true;
        }
        return _.some(
            learningOpportunity.allowedPaths,
            allowedPath => _.get(allowedPath, educationOption.educationPathKey) === educationOption.moduleGroupId,
        );
    }

    static isEducationOptionIncludedInStudyRightAcceptedSelectionPath(educationOption: GenericPlanEducationOption, studyRight: StudyRight) {
        return _.get(studyRight.acceptedSelectionPath, educationOption.educationPathKey) === educationOption.moduleGroupId;
    }

    static isEducationOptionSelectableBasedOnStudyRightAllowedSelectionPaths(
        educationOption: GenericPlanEducationOption,
        studyRight: StudyRight,
        education: Education,
    ) {
        const selectionPathToBeSelected = this.getSelectionPathToBeSelected(educationOption, studyRight);
        return _.some(this.getStudyRightSelectionPaths(education, studyRight), studyRightSelectionPath =>
            this.selectionPathIsPartialMatch(selectionPathToBeSelected, studyRightSelectionPath),
        );
    }

    static getSelectionPathToBeSelected(educationOption: GenericPlanEducationOption, studyRight: StudyRight) {
        const selectionPath = _.clone(studyRight.acceptedSelectionPath);
        const indexOfLastProperty = _.indexOf(this.propertyNames, educationOption.educationPathKey);
        _.forEach(this.propertyNames, propertyName => {
            if (educationOption.educationPathKey === propertyName) {
                _.set(selectionPath, propertyName, educationOption.moduleGroupId);
            }
            if (_.indexOf(this.propertyNames, propertyName) > indexOfLastProperty) {
                _.unset(selectionPath, propertyName);
            }
        });
        return selectionPath;
    }

    static getEducationOptionsByPathKeyForSelectionPath(
        educationOptions: GenericPlanEducationOption[],
        selectionPath: LearningOpportunitySelectionPath,
    ) {
        const result = {};
        _.forEach(this.propertyNames, propertyName => {
            const moduleGroupId = _.get(selectionPath, propertyName);
            if (moduleGroupId) {
                let educationOption;
                if (_.indexOf(this.propertyNames, propertyName) % 2 === 0) {
                    educationOption = _.find(educationOptions, eo => eo.moduleGroupId === _.get(selectionPath, propertyName));
                } else {
                    const parentPathKey = this.getPreviousEducationPathKey(propertyName);
                    educationOption = _.find(educationOptions, eo => {
                        const moduleGroupIdMatches = eo.moduleGroupId === _.get(selectionPath, propertyName);
                        const parentModuleGroupIdMatches =
                            (eo as PlanEducationChildOption).parentModuleGroupId === _.get(selectionPath, parentPathKey);
                        return moduleGroupIdMatches && parentModuleGroupIdMatches;
                    });
                }
                if (educationOption) {
                    _.set(result, propertyName, educationOption);
                }
            }
        });
        return result;
    }

    static getEducationOptionsToBeChanged(
        oldOptions: { [educationPathKey: string]: GenericPlanEducationOption },
        newOptions: { [educationPathKey: string]: GenericPlanEducationOption },
    ) {
        const result: GenericPlanEducationOption[] = [];
        _.forEach(_.keys(oldOptions), oldOptionKey => {
            const oldOption = _.get(oldOptions, oldOptionKey);
            if (!_.isEqual(oldOption, _.get(newOptions, oldOptionKey))) {
                result.push(oldOption);
            }
        });
        return result;
    }

    static getBlockingEducationOptions(
        educationOptionToBeSelected: GenericPlanEducationOption,
        educationOptions: GenericPlanEducationOption[],
        studyRight: StudyRight,
    ) {
        const oldOptions = this.getEducationOptionsByPathKeyForSelectionPath(educationOptions, studyRight.acceptedSelectionPath);
        const newSelectionPath = this.getSelectionPathToBeSelected(educationOptionToBeSelected, studyRight);
        const newOptions = this.getEducationOptionsByPathKeyForSelectionPath(educationOptions, newSelectionPath);
        const educationOptionsToBeChanged = this.getEducationOptionsToBeChanged(oldOptions, newOptions);
        return _.filter(educationOptionsToBeChanged, option => option.acceptanceType !== 'AUTOMATIC');
    }

    // isModuleSelectedAsMinor regards minors all modules that are selected under a module with
    // contentFilter minor-study-right. module's studyRightSelectionType property is ignored here on purpose

    static isModuleSelectedAsMinor(module: EntityWithRule, validatablePlan: ValidatablePlan) {
        const parentModule = validatablePlan.getParentModuleOrCustomModuleAttainmentForModule(module) as EntityWithRule;
        if (!parentModule) {
            return false;
        }
        return this.doesModuleContainOnlyMinors(parentModule);
    }

    static doesModuleContainOnlyMinors(module: EntityWithRule) {
        const studyRightSelectionType = _.get(module, 'contentFilter.studyRightSelectionType');
        return studyRightSelectionType === 'urn:code:study-right-selection-type:minor-study-right';
    }
}
